import type { GetStaticPathsContext, GetStaticPropsContext } from 'next'
import { useRouter } from 'next/router'
import commerce from '@lib/api/commerce'
import { Layout } from '@components/common'
import { ProductView } from '@components/product'
import getDefaultVariantSlug from '@utils/get-default-variant-slug'
import { Product } from '@commerce/types/product'
import Head from 'next/head'
import { getMetalValueFromSlug } from '@components/product/helpers'
import { ProductVariant } from '@commerce/types/product'
import { titleize } from '@utils/capitalize-strings'
import productsRedirects from '@components/product/products_redirects.json'
import getDiamondIdFromProductSku from '@utils/get-diamond-id-from-product-sku'
import Script from 'next/script'
import { useEffect } from 'react'

// product status
const ARCHIVED : string = 'archived'

const CREATE_ENGAGEMENT_RING = '/t/create-your-ring'

function generateProductMicroData(product: Product, slug: string) {
  let productOrVariant: Product | ProductVariant

  if (product.variants && product.variants.length > 0) {
    const result = product.variants.filter(
      (variant) => variant.slug.toLowerCase() === slug.toLowerCase()
    )

    if (result.length > 0) {
      productOrVariant = result[0]
    } else {
      // Use the product data in case the slug doesn't match any variant.
      productOrVariant = product
    }
  } else {
    // Use the product data in case the product doesn't have variants.
    productOrVariant = product
  }

  const metalValue = getMetalValueFromSlug(product, slug)

  const microData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: `${productOrVariant.name}${
      metalValue !== '' ? ` - ${titleize(metalValue)}` : ''
    }`,
    image: productOrVariant.images?.map((img) => img.url),
    description: `${productOrVariant.name}${
      metalValue !== '' ? ` - ${titleize(metalValue)}` : ''
    }`,
    sku: `${productOrVariant.sku}`,
    brand: {
      '@type': 'Brand',
      name: 'Yadav Diamonds and Jewelry',
    },
    offers: {
      '@type': 'Offer',
      url: `${process.env.baseUrl}/product/${productOrVariant.slug}`,
      priceCurrency: 'USD',
      price: `${productOrVariant.price.value}`,
      availability: 'https://schema.org/InStock',
    },
  }

  return JSON.stringify(microData)
}

export async function getStaticProps({
  params,
  locale,
  locales,
  preview,
}: GetStaticPropsContext<{ slug: string }>) {
  const slugProduct = `/product/${params!.slug}`

  // Check if the slug is present in the data
  const slugProductRedirect = productsRedirects.find(
    (redirect: { from: string; to: string }) => redirect.from === slugProduct
  )
  //Redirect if the product exists in the redirect list
  if (slugProductRedirect) {
    console.log(
      `\nProduct not found ( ${slugProduct} ). Redirecting to ${slugProductRedirect}.\n`
    )
    return {
      redirect: {
        destination: slugProductRedirect.to,
        permanent: true,
      },
    }
  }

  try {
    const config = { locale, locales }
    const slug = getDefaultVariantSlug(params!.slug)
    const productPromise = commerce.getProduct({
      variables: { slug, imagesSize: '2000x2000' },
      config,
      preview,
    })

    const { product } = await productPromise

    //Redirect to diamond URL if the product is a gemstone
    if (
      product &&
      product.sku &&
      product.cyo_type &&
      product.cyo_type.toLowerCase() === 'gemstone'
    ) {
      const diamondId = getDiamondIdFromProductSku(product.sku)
      if (diamondId) {
        return {
          redirect: {
            destination: `/diamond/${diamondId}`,
            permanent: true,
          },
        }
      }
    }

    // Products can belong to multiple taxons
    // It should only have one leaf, but some have more
    // In those cases, use the first one.
    // Default fallback: create engagament ring

    const taxonsLeaf = product?.taxons.filter((taxon)=>{
      if(taxon.isLeaf) return taxon
    })
    const taxon = taxonsLeaf?.[0];

    // archived -> redirect to parent taxon page
    // draft -> product page
    if (!product  || (!product.available && product.status?.toLowerCase() === ARCHIVED) ) {
      const destination = taxon?.permalink
        ? `/t/${taxon.permalink}`
        : CREATE_ENGAGEMENT_RING
      console.log(
        `\nProduct not found ${slugProduct}. Redirecting to parent taxon page - ${destination}.\n`
      )
      return {
        /* revalidate: 10,
        redirect: {
          destination: destination,
          permanent: false,
        }, */
        props: {
          shouldRedirect: true,
          redirectDestination: destination,
          product: null,
          relatedProducts: [],
          productMicroData: '',
        },
        revalidate: 10,
      }
    }

    const productMicroData = generateProductMicroData(product, params!.slug)

    if (!taxon) {
      return {
        props: {
          product,
          relatedProducts: [],
          productMicroData,
        },
        revalidate: 10,
      }
    }
    const allProductsPromise = commerce.getAllProducts({
      variables: {
        taxons: [Number(taxon?.id)],
        first: 4,
        imagesSize: '360x360',
      },
      config,
      preview,
    })

    const { products: relatedProducts } = await allProductsPromise

    return {
      props: {
        product,
        relatedProducts,
        productMicroData,
      },
      revalidate: 10,
    }

    // In case the Spree API raises an error when looking for a product, show the 404 page.
    // TODO: handle different error types.
  } catch (error) {
    console.log(
      '\nError product slug: ',
      params?.slug,
      '. Redirecting to create your ring page.\n'
    )
    return {
      /* revalidate: 10,
      redirect: {
        destination: CREATE_ENGAGEMENT_RING,
        permanent: false,
      }, */
      props: {
        shouldRedirect: true,
        redirectDestination: CREATE_ENGAGEMENT_RING,
        product: null,
        relatedProducts: [],
        productMicroData: '',
      },
      revalidate: 10,
    }
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default function Slug({
  product,
  relatedProducts,
  productMicroData,
  shouldRedirect,
  redirectDestination,
}: {
  product: Product | null
  relatedProducts: Product[]
  productMicroData: string
  shouldRedirect?: boolean
  redirectDestination?: string
}) {
  const router = useRouter()

  useEffect(() => {
    if (shouldRedirect && redirectDestination) {
      router.replace(redirectDestination)
    }
  }, [shouldRedirect, redirectDestination, router])

  return (router.isFallback || !product) ? (
    <h1>Loading...</h1>
  ) : (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: productMicroData }}
          key="product-jsonld"
        />
      </Head>
      <ProductView product={product} relatedProducts={relatedProducts} />
    </>
  )
}

Slug.Layout = Layout
